import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";
import { getProject, staticImg } from "@/lib/api";

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getProject(slug)
      .then((p) => setProject(p))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-arven-offwhite/50">Loading...</div>;
  }
  if (!project) {
    return (
      <Container className="py-32 text-center">
        <p className="text-arven-offwhite/60">Project not found.</p>
        <Link to="/projets" className="btn-text-gold mt-6">{t.projects.detail.back}</Link>
      </Container>
    );
  }

  return (
    <div data-testid="project-detail-page">
      <Container className="pt-6 pb-2">
        <Link to="/projets" className="btn-text-gold" data-testid="back-to-projects">
          <ArrowLeft size={14} /> {t.projects.detail.back}
        </Link>
      </Container>

      <Section className="pt-8">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
            <div className="lg:col-span-5">
              <Reveal><Eyebrow>{`${project.city} — ${project.country}`}</Eyebrow></Reveal>
              <Reveal delay={0.1}>
                <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-6">
                  {lang === "fr" ? project.title : project.title_en}
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-[15px] md:text-base leading-relaxed text-arven-offwhite/75">
                  {lang === "fr" ? project.description : project.description_en}
                </p>
              </Reveal>

              <Reveal delay={0.3}>
                <dl className="mt-10 space-y-5">
                  {[
                    [t.projects.detail.city, `${project.city}, ${project.country}`],
                    [t.projects.detail.year, String(project.year)],
                    [t.projects.detail.surface, project.surface],
                    [t.projects.detail.collection, project.collection],
                    [t.projects.detail.style, lang === "fr" ? project.style : project.style_en],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-baseline gap-6 border-b border-white/10 pb-3">
                      <dt className="w-32 label-eyebrow">{k}</dt>
                      <dd className="text-[15px] text-arven-offwhite/85 capitalize">{v}</dd>
                    </div>
                  ))}
                </dl>
              </Reveal>

              <Reveal delay={0.4}>
                <div className="mt-8">
                  <p className="label-eyebrow mb-3">{t.projects.detail.materials}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.materials.map((m) => (
                      <span key={m} className="px-3 py-1.5 text-[11px] tracking-[0.18em] uppercase border border-white/15 rounded-full">
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal>
                <div className="relative overflow-hidden rounded-2xl aspect-[4/5] md:aspect-[4/5] kbd-card">
                  <img src={staticImg(project.image)} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.2}>
            <p className="serif text-2xl md:text-3xl lg:text-4xl text-arven-offwhite/85 mt-24 max-w-3xl leading-snug">
              « Chaque détail a ét́ penś pour cŕer un espace éĺgant, fonctionnel et intemporel. »
            </p>
          </Reveal>
        </Container>
      </Section>

      <Section className="bg-arven-anthracite border-t border-white/5 noise-overlay relative">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-6 md:py-12">
            <Reveal>
              <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight">
                {t.collections.cta_h2.split("\n")[0]}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex justify-center gap-4 flex-wrap">
                <Link to="/rendez-vous" className="btn-gold">{t.common.appointment} <ArrowRight size={14} /></Link>
                <Link to="/projets" className="btn-outline-gold">{t.projects.detail.back}</Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
